import { convertProjectBudgetToUsd } from "./projectFilters.js";
import { budgetHealth, selectNextMilestone, signalDate } from "../../lib/projectDisplaySignals.js";

const RISK_ORDER = { critical: 0, at_risk: 1, on_track: 2 };

export function portfolioDate(value) {
  return signalDate(value);
}

export function selectProjectTarget(project) {
  const exiting = project?.projectType === "exit" || project?.projectType === "consolidation";
  return {
    date: portfolioDate(exiting ? project?.exitDate : project?.fdobDate),
    label: exiting ? "Exit date" : "FDOB",
  };
}

function finiteNumber(value) {
  if (value == null || value === "" || typeof value === "boolean") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

export function selectProjectSeats(project) {
  const linkedDelta = finiteNumber(project?.projectSeatDelta);
  if (linkedDelta != null) return linkedDelta;
  // A future office capacity is not necessarily this project's contribution.
  // Exit evidence is the one unambiguous lifecycle fallback.
  if ((project?.projectType === "exit" || project?.projectType === "consolidation")
    && portfolioDate(project?.exitDate)) {
    const beforeExit = finiteNumber(project.exitCapacityBefore);
    if (beforeExit != null) return beforeExit === 0 ? 0 : -beforeExit;
  }
  return null;
}

export function selectNextPortfolioMilestone(milestones) {
  return selectNextMilestone(milestones);
}

export function enrichPortfolioProject(project, { rates = null, canSeeBudget = true } = {}) {
  const target = selectProjectTarget(project);
  const milestone = project?.nextMilestone;
  const budget = finiteNumber(project?.approvedBudget);
  const budgetUsd = canSeeBudget && project.financialsTracked !== false && budget != null
    ? convertProjectBudgetToUsd(project, rates)
    : null;
  return {
    ...project,
    portfolio: {
      targetDate: target.date,
      targetLabel: target.label,
      milestone: milestone?.name ? { label: milestone.name, date: portfolioDate(milestone.dueDate) } : null,
      milestoneError: Boolean(project?.milestonesError),
      seats: selectProjectSeats(project),
      budgetUsd,
      budgetHealth: budgetUsd == null || !canSeeBudget || project.financialsTracked === false
        ? budgetHealth(null, null) : budgetHealth(budget, project.forecastAtCompletion),
      budgetState: !canSeeBudget ? "restricted"
        : project.financialsTracked === false ? "not_tracked"
        : budget == null ? "missing_budget"
          : budgetUsd == null ? "missing_rate" : "available",
    },
  };
}

export function comparePortfolioTargets(a, b, direction = 1) {
  const ad = selectProjectTarget(a).date;
  const bd = selectProjectTarget(b).date;
  if ((ad == null) !== (bd == null)) return ad == null ? 1 : -1;
  return (ad && bd ? ad.localeCompare(bd) * direction : 0)
    || (RISK_ORDER[a.statusOverall] ?? 3) - (RISK_ORDER[b.statusOverall] ?? 3)
    || String(a.name || "").localeCompare(String(b.name || ""))
    || String(a.id).localeCompare(String(b.id));
}
